// /orgs/$orgSlug/projects/$projectId/review-sessions/$sessionId
// (PRD §9.10, B7).
//
// Route shell for the live in-session experience. Project access is
// enforced server-side (the socket runs its own tenancy gate, and the
// session list query 403/404s); surface those here so a bad deep link
// shows a useful page rather than the default error boundary.

import { createFileRoute, Link } from '@tanstack/react-router';

import { ApiError, apiErrorMessage } from '../api/client';
import { LiveReviewPage } from '../components/LiveReviewPage';

export const Route = createFileRoute(
  '/orgs/$orgSlug/projects/$projectId/review-sessions/$sessionId'
)({
  component: LiveReviewRoute,
  errorComponent: LiveReviewRouteError,
});

function LiveReviewRoute(): React.JSX.Element {
  const { orgSlug, projectId, sessionId } = Route.useParams();
  return (
    <LiveReviewPage
      orgSlug={orgSlug}
      projectId={projectId}
      sessionId={sessionId}
    />
  );
}

function LiveReviewRouteError({
  error,
}: {
  error: unknown;
}): React.JSX.Element {
  const isApi = error instanceof ApiError;
  const status = isApi ? error.status : null;
  const heading =
    status === 403
      ? 'You don’t have access to this session'
      : status === 404
        ? 'Review session not found'
        : 'Something went wrong loading this review session';
  const body = apiErrorMessage(error, 'Try again, or pick a different session from the review sessions list.');

  return (
    <div className="mx-auto max-w-2xl py-7">
      <p className="text-text-faint text-11 mb-2 uppercase tracking-wider">
        Error
      </p>
      <h1 className="text-text text-28 mb-3 font-semibold">{heading}</h1>
      <p className="text-text-muted mb-5 text-13">{body}</p>
      <Link
        to="/"
        className="bg-accent text-accent-fg border-border-strong rounded-2 hover:bg-accent-hi inline-block border px-3 py-2 text-13 transition-colors"
      >
        Back to home
      </Link>
    </div>
  );
}
